import { PolicyHolderRelationship, InsuranceSex } from './NewBookings.enums'

/**
 * @shared Used by:
 *   - V2 Booking: components/app/NewBookings/* (origin)
 *   - Insurance Management:
 *       - app/insurance-management/utils/buildClientList.ts (picker filter,
 *         icon/label maps)
 *       - app/insurance-management/utils/buildSavePayload.ts (CHILD-flow
 *         AMD mapping identity pass-through depends on `SELF = 'Self'`)
 *       - app/insurance-management/flows/client/steps/PolicyholderStep.tsx
 *
 * The string values ('Self', 'Spouse', 'Child', 'other') are AMD-recognized
 * descriptions and must stay stable. Renaming any value will silently break
 * IM's AMD mapping (CHILD-patient codes go wrong by one generation — the
 * exact bug class THRIV-13514 fixed). Coordinate with IM before changes.
 */
export enum RelationshipEnum {
  SELF = 'Self',
  SPOUSE = 'Spouse',
  MY_CHILD = 'Child',
  OTHER = 'other',
}

export const RELATIONSHIP_SELECTOR_MAPPING = {
  [RelationshipEnum.SELF]: {
    icon: '/images/relationship_selector_myself.svg',
    text: 'Myself',
  },
  [RelationshipEnum.MY_CHILD]: {
    icon: '/images/relationship_selector_child.svg',
    text: 'A child',
  },
}

export const AVAILABLE_RELATIONSHIP_MAP = [
  {
    icon: '/images/relationship_selector_spouse.svg',
    text: 'My Spouse',
    card_text: 'SPOUSE',
    id: 'my_spouse',
  },
  {
    icon: '/images/relationship_selector_child.svg',
    text: 'My Child',
    card_text: 'CHILD',
    id: 'my_child',
  },
  {
    icon: '/images/relationship_selector_other.svg',
    text: 'Other',
    card_text: 'OTHER',
    id: 'other',
  },
]

export const AVAILABLE_RELATIONSHIP_EXCLUSIONS = ['my_spouse']

// TODO: this data will be dynamic and api bound
export const GENDER_OPTIONS = [
  {
    label: 'Male',
    value: 'male',
  },
  {
    label: 'Female',
    value: 'female',
  },
]

export const STATE_OPTIONS = [
  {
    label: 'CA',
    value: 'california',
  },
  {
    label: 'NY',
    value: 'New York',
  },
]

export const FAMILY_COUSELING_SQUIGGLE_PATH = '/images/squiggle.svg'

export const initialPatientFormData: PatientEntity = {
  firstName: '',
  lastName: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    zipcode: '',
  },
  dateOfBirth: '',
  email: '',
  relationship: '',
  isEditing: true,
}

export const CARD_MAP = {
  VISA: { label: 'Visa', icon: '/icons/booking-v2-card-visa.svg' },
  MC: {
    label: 'Master Card',
    icon: '/icons/booking-v2-card-mastercard.svg',
  },
  AMEX: {
    label: 'American Express',
    icon: '/icons/booking-v2-card-american-express.svg',
  },
  DISC: { label: 'Discover', icon: '/icons/booking-v2-card-discover.svg' },
}

export const AMD_PAY_SUBMIT_BUTTON = 'amd-pay-submit-button'

export type PolicyHolderDetails = {
  relationship: PolicyHolderRelationship
  gender: InsuranceSex
}

export const bookingPageRoute = {
  INITIAL: '/bookings/new',
  CLIENT_DETAILS: '/bookings/new/client-details',
  INSURANCE: '/bookings/new/insurance',
  BILLING: '/bookings/new/billing',
  CONSENTS: '/bookings/new/consents',
  COMPLETED: '/bookings/completed',
}

export const clientDetailsRoutes: string[] = [
  bookingPageRoute.CLIENT_DETAILS + '/individual',
  bookingPageRoute.CLIENT_DETAILS + '/med-psy',
  bookingPageRoute.CLIENT_DETAILS + '/couples',
  bookingPageRoute.CLIENT_DETAILS + '/family',
]

export const insuranceDetailsRoutes: string[] = [
  bookingPageRoute.INSURANCE + '/self-pay',
  bookingPageRoute.INSURANCE + '/coverage-details',
  bookingPageRoute.INSURANCE + '/insurance-list',
]

export const consentsRoutes: string[] = [
  bookingPageRoute.CONSENTS + '/contact-info',
  bookingPageRoute.CONSENTS + '/agreement',
  bookingPageRoute.CONSENTS + '/completed',
]

// Combined routes for prefetching - excludes base CONSENTS route since it has no page.tsx
export const prefetchRoutes: string[] = [
  bookingPageRoute.INITIAL,
  bookingPageRoute.CLIENT_DETAILS,
  bookingPageRoute.INSURANCE,
  bookingPageRoute.BILLING,
  bookingPageRoute.COMPLETED,
  ...clientDetailsRoutes,
  ...insuranceDetailsRoutes,
  ...consentsRoutes,
]

export enum BookingPageType {
  CLIENT_DETAILS = 'CLIENT_DETAILS',
  INSURANCE = 'INSURANCE',
  BILLING = 'BILLING',
  CONSENTS = 'CONSENTS',
}

export const BOOKING_SECTIONS: BookingPageType[] = [
  BookingPageType.CLIENT_DETAILS,
  BookingPageType.INSURANCE,
  BookingPageType.BILLING,
  BookingPageType.CONSENTS,
]

export const cardTitles = {
  [BookingPageType.CLIENT_DETAILS]: {
    DEFAULT_TITLE: 'Client details',
    FIRST_TIME_USER_OPEN_CARD_TITLE: "Let's start with your details",
    RETURNING_USER_OPEN_CARD_TITLE: 'Welcome back',
    PROGRESS_BAR_LABEL: 'Details',
  },
  [BookingPageType.INSURANCE]: {
    DEFAULT_TITLE: 'Session coverage',
    FIRST_TIME_USER_OPEN_CARD_TITLE: "Next, we'll check your coverage",
    RETURNING_USER_OPEN_CARD_TITLE: 'Your session coverage',
    INSURANCE_LIST_OPEN_CARD_TITLE: 'Select your session coverage',
    PROGRESS_BAR_LABEL: 'Coverage',
  },
  [BookingPageType.BILLING]: {
    DEFAULT_TITLE: 'Billing information',
    FIRST_TIME_USER_OPEN_CARD_TITLE: "Let's get your billing information",
    RETURNING_USER_OPEN_CARD_TITLE: 'Confirm your form of payment',
    PROGRESS_BAR_LABEL: 'Billing',
  },
  [BookingPageType.CONSENTS]: {
    DEFAULT_TITLE: 'Consent forms',
    FIRST_TIME_USER_OPEN_CARD_TITLE: 'Review and complete consents',
    RETURNING_USER_OPEN_CARD_TITLE: 'Review your consents',
    PROGRESS_BAR_LABEL: 'Consents',
  },
}

export const MOBILE_CARD_CONFIG = [
  {
    section: BookingPageType.CLIENT_DETAILS,
    title: 'Client details',
    subHeader: (providerFirstName: string): string =>
      `We\u2019ll pass this info along to ${providerFirstName} and set up your account.`,
  },
  {
    section: BookingPageType.INSURANCE,
    title: 'Session Coverage',
    subHeader:
      'If you plan to cover sessions with insurance, have your insurance card handy for this part',
  },
  {
    section: BookingPageType.BILLING,
    title: 'Billing details',
    subHeader:
      'We\u2019ll save your credit card details to cover any out-of-pocket costs for your session',
  },
  {
    section: BookingPageType.CONSENTS,
    title: 'Consents',
    subHeader:
      'Review and sign important documents so you can fully understand your rights as a client',
  },
]

export const duplicatePatientErrorMessage =
  "Oops, it looks like you've already added this person."

export const POLICY_HOLDER_OPTIONS = {
  SELF: {
    icon: '/icons/Myself.svg',
    label: 'Myself',
    value: PolicyHolderRelationship.SELF,
  },
  SPOUSE: {
    icon: '/icons/mySpouse.svg',
    label: 'My spouse',
    value: PolicyHolderRelationship.SPOUSE,
  },
  PARENT: {
    icon: '/icons/Parent.svg',
    label: 'My parent',
    value: PolicyHolderRelationship.PARENT,
  },
  MY_CHILD: {
    icon: '/icons/Parent.svg',
    label: 'My child',
    value: PolicyHolderRelationship.MY_CHILD,
  },
  OTHER: {
    icon: '/icons/mySpouse.svg',
    label: 'Other legal guardian',
    value: PolicyHolderRelationship.OTHER,
  },
}

export const PERCEIVED_AMD_PAY_LOAD_DELAY = 1000
